import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../services/api'

// ==================== ASYNC THUNKS ====================

// Obtener todo el material con filtros
export const fetchMaterial = createAsyncThunk(
  'material/fetchAll',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/material', { params })
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Error al obtener material')
    }
  }
)

// Obtener estadísticas del material
export const fetchMaterialEstadisticas = createAsyncThunk(
  'material/fetchEstadisticas',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/material/estadisticas')
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Error al obtener estadísticas')
    }
  }
)

// Obtener alertas de material
export const fetchMaterialAlertas = createAsyncThunk(
  'material/fetchAlertas',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/material/alertas')
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Error al obtener alertas')
    }
  }
)

// Obtener un material por ID
export const fetchMaterialById = createAsyncThunk(
  'material/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/material/${id}`)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Error al obtener material')
    }
  }
)

// Obtener historial de asignaciones de un material
export const fetchMaterialHistorial = createAsyncThunk(
  'material/fetchHistorial',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/material/${id}/historial`)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Error al obtener historial')
    }
  }
)

// Crear nuevo material
export const createMaterial = createAsyncThunk(
  'material/create',
  async (materialData, { rejectWithValue }) => {
    try {
      const response = await api.post('/material', materialData)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Error al crear material')
    }
  }
)

// Actualizar material
export const updateMaterial = createAsyncThunk(
  'material/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/material/${id}`, data)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Error al actualizar material')
    }
  }
)

// Desactivar material (soft delete)
export const deleteMaterial = createAsyncThunk(
  'material/delete',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/material/${id}`)
      return { id, ...response.data }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Error al desactivar material')
    }
  }
)

// Asignar material
export const asignarMaterial = createAsyncThunk(
  'material/asignar',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/material/${id}/asignar`, data)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Error al asignar material')
    }
  }
)

// Devolver material
export const devolverMaterial = createAsyncThunk(
  'material/devolver',
  async ({ asignacionId, observaciones }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `/material/asignaciones/${asignacionId}/devolver`,
        { observaciones }
      )
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Error al devolver material')
    }
  }
)

// Dividir material (solo para tipo cantidad)
export const dividirMaterial = createAsyncThunk(
  'material/dividir',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/material/${id}/dividir`, data)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Error al dividir material')
    }
  }
)

// Actualización inline de un campo específico
export const updateMaterialInline = createAsyncThunk(
  'material/updateInline',
  async ({ id, campo, valor }, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/material/${id}/inline`, { campo, valor })
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Error al actualizar material')
    }
  }
)

// Nueva asignación (con soporte para carro y cajonera)
export const asignarMaterialNuevo = createAsyncThunk(
  'material/asignarNuevo',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/material/${id}/asignar-nuevo`, data)
      return { materialId: id, asignacion: response.data }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Error al asignar material')
    }
  }
)

// ==================== ALERTAS ====================

// Obtener alertas de un material
export const fetchMaterialAlertasById = createAsyncThunk(
  'material/fetchAlertasById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/material/${id}/alertas`)
      return { materialId: id, alertas: response.data }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Error al obtener alertas')
    }
  }
)

// Crear alerta
export const crearAlertaMaterial = createAsyncThunk(
  'material/crearAlerta',
  async ({ materialId, data }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/material/${materialId}/alertas`, data)
      return { materialId, alerta: response.data }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Error al crear alerta')
    }
  }
)

// Completar/actualizar alerta
export const actualizarAlertaMaterial = createAsyncThunk(
  'material/actualizarAlerta',
  async ({ alertaId, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/material/alertas/${alertaId}`, data)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Error al actualizar alerta')
    }
  }
)

// Eliminar alerta
export const eliminarAlertaMaterial = createAsyncThunk(
  'material/eliminarAlerta',
  async (alertaId, { rejectWithValue }) => {
    try {
      await api.delete(`/material/alertas/${alertaId}`)
      return alertaId
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Error al eliminar alerta')
    }
  }
)

// ==================== SLICE ====================

const materialSlice = createSlice({
  name: 'material',
  initialState: {
    items: [],
    selectedMaterial: null,
    historial: null,
    estadisticas: null,
    alertas: null,
    materialAlertas: {}, // { materialId: [alertas] }
    pagination: {
      page: 1,
      limit: 50,
      total: 0,
      pages: 0
    },
    loading: false,
    estadisticasLoading: false,
    alertasLoading: false,
    error: null
  },
  reducers: {
    clearSelectedMaterial: (state) => {
      state.selectedMaterial = null
      state.historial = null
    },
    clearError: (state) => {
      state.error = null
    },
    setPage: (state, action) => {
      state.pagination.page = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch All
      .addCase(fetchMaterial.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchMaterial.fulfilled, (state, action) => {
        state.loading = false
        state.items = action.payload.material
        state.pagination = action.payload.pagination
      })
      .addCase(fetchMaterial.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.error || 'Error al obtener material'
      })

      // Fetch Estadísticas — flag propio: los KPIs ya vienen calientes del
      // Dashboard y no deben quedar tapados por el spinner de la lista.
      .addCase(fetchMaterialEstadisticas.pending, (state) => {
        state.estadisticasLoading = true
      })
      .addCase(fetchMaterialEstadisticas.fulfilled, (state, action) => {
        state.estadisticasLoading = false
        state.estadisticas = action.payload
      })
      .addCase(fetchMaterialEstadisticas.rejected, (state, action) => {
        state.estadisticasLoading = false
        state.error = action.payload?.error || 'Error al obtener estadísticas'
      })

      // Fetch Alertas — flag propio: el escaneo pesado de /material/alertas no
      // debe mantener la tabla en spinner mientras corre.
      .addCase(fetchMaterialAlertas.pending, (state) => {
        state.alertasLoading = true
      })
      .addCase(fetchMaterialAlertas.fulfilled, (state, action) => {
        state.alertasLoading = false
        state.alertas = action.payload
      })
      .addCase(fetchMaterialAlertas.rejected, (state, action) => {
        state.alertasLoading = false
        state.error = action.payload?.error || 'Error al obtener alertas'
      })

      // Fetch By ID
      .addCase(fetchMaterialById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchMaterialById.fulfilled, (state, action) => {
        state.loading = false
        state.selectedMaterial = action.payload
      })
      .addCase(fetchMaterialById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.error || 'Error al obtener material'
      })

      // Fetch Historial
      .addCase(fetchMaterialHistorial.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchMaterialHistorial.fulfilled, (state, action) => {
        state.loading = false
        state.historial = action.payload
      })
      .addCase(fetchMaterialHistorial.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.error || 'Error al obtener historial'
      })

      // Create
      .addCase(createMaterial.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createMaterial.fulfilled, (state, action) => {
        state.loading = false
        state.items.unshift(action.payload)
        state.pagination.total += 1
      })
      .addCase(createMaterial.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.error || 'Error al crear material'
      })

      // Update
      .addCase(updateMaterial.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateMaterial.fulfilled, (state, action) => {
        state.loading = false
        state.selectedMaterial = action.payload
        const index = state.items.findIndex(m => m.id === action.payload.id)
        if (index !== -1) {
          state.items[index] = action.payload
        }
      })
      .addCase(updateMaterial.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.error || 'Error al actualizar material'
      })

      // Update Inline
      .addCase(updateMaterialInline.fulfilled, (state, action) => {
        const index = state.items.findIndex(m => m.id === action.payload.id)
        if (index !== -1) {
          state.items[index] = action.payload
        }
      })
      .addCase(updateMaterialInline.rejected, (state, action) => {
        state.error = action.payload?.error || 'Error al actualizar material'
      })

      // Delete (soft)
      .addCase(deleteMaterial.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteMaterial.fulfilled, (state, action) => {
        state.loading = false
        state.items = state.items.filter(m => m.id !== action.payload.id)
        state.pagination.total -= 1
      })
      .addCase(deleteMaterial.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.error || 'Error al desactivar material'
      })

      // Dividir Material
      .addCase(dividirMaterial.fulfilled, (state, action) => {
        // Actualizar material original
        const index = state.items.findIndex(m => m.id === action.payload.materialOriginal.id)
        if (index !== -1) {
          state.items[index] = action.payload.materialOriginal
        }
        // Agregar nuevo material
        state.items.unshift(action.payload.nuevoMaterial)
        state.pagination.total += 1
      })
      .addCase(dividirMaterial.rejected, (state, action) => {
        state.error = action.payload?.error || 'Error al dividir material'
      })

      // Asignar Nuevo
      .addCase(asignarMaterialNuevo.fulfilled, (state, action) => {
        const { materialId, asignacion } = action.payload
        const index = state.items.findIndex(m => m.id === materialId)
        if (index !== -1) {
          if (!state.items[index].asignaciones) {
            state.items[index].asignaciones = []
          }
          state.items[index].asignaciones.push(asignacion)
          state.items[index].estado = 'En Uso'
          state.items[index].asignadoANombre = asignacion.bomberoNombre || asignacion.carroNombre
          state.items[index].asignadoATipo = asignacion.bomberoId ? 'bombero' : 'carro'
        }
      })
      .addCase(asignarMaterialNuevo.rejected, (state, action) => {
        state.error = action.payload?.error || 'Error al asignar material'
      })

      // Asignar (legacy)
      .addCase(asignarMaterial.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(asignarMaterial.fulfilled, (state, action) => {
        state.loading = false
        const index = state.items.findIndex(m => m.id === action.payload.materialId)
        if (index !== -1) {
          if (!state.items[index].asignaciones) {
            state.items[index].asignaciones = []
          }
          state.items[index].asignaciones.push(action.payload)
        }
      })
      .addCase(asignarMaterial.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.error || 'Error al asignar material'
      })

      // Devolver
      .addCase(devolverMaterial.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(devolverMaterial.fulfilled, (state, action) => {
        state.loading = false
        const materialId = action.payload.materialId
        const index = state.items.findIndex(m => m.id === materialId)
        if (index !== -1 && state.items[index].asignaciones) {
          const asigIndex = state.items[index].asignaciones.findIndex(
            a => a.id === action.payload.id
          )
          if (asigIndex !== -1) {
            state.items[index].asignaciones[asigIndex] = action.payload
          }
          // Actualizar estado si no quedan asignaciones activas
          const asignacionesActivas = state.items[index].asignaciones.filter(a => a.activo)
          if (asignacionesActivas.length === 0) {
            state.items[index].estado = 'Disponible'
            state.items[index].asignadoANombre = null
            state.items[index].asignadoATipo = null
          }
        }
      })
      .addCase(devolverMaterial.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.error || 'Error al devolver material'
      })

      // Alertas por material
      .addCase(fetchMaterialAlertasById.fulfilled, (state, action) => {
        state.materialAlertas[action.payload.materialId] = action.payload.alertas
      })

      // Crear alerta
      .addCase(crearAlertaMaterial.fulfilled, (state, action) => {
        const { materialId, alerta } = action.payload
        // Agregar a materialAlertas
        if (!state.materialAlertas[materialId]) {
          state.materialAlertas[materialId] = []
        }
        state.materialAlertas[materialId].push(alerta)
        // Agregar al item en la lista
        const index = state.items.findIndex(m => m.id === materialId)
        if (index !== -1) {
          if (!state.items[index].alertas) {
            state.items[index].alertas = []
          }
          state.items[index].alertas.push(alerta)
        }
      })

      // Actualizar alerta
      .addCase(actualizarAlertaMaterial.fulfilled, (state, action) => {
        const alerta = action.payload
        // Actualizar en materialAlertas
        for (const materialId in state.materialAlertas) {
          const idx = state.materialAlertas[materialId].findIndex(a => a.id === alerta.id)
          if (idx !== -1) {
            state.materialAlertas[materialId][idx] = alerta
            break
          }
        }
        // Actualizar en items
        for (const item of state.items) {
          if (item.alertas) {
            const idx = item.alertas.findIndex(a => a.id === alerta.id)
            if (idx !== -1) {
              item.alertas[idx] = alerta
              break
            }
          }
        }
      })

      // Eliminar alerta
      .addCase(eliminarAlertaMaterial.fulfilled, (state, action) => {
        const alertaId = action.payload
        // Eliminar de materialAlertas
        for (const materialId in state.materialAlertas) {
          state.materialAlertas[materialId] = state.materialAlertas[materialId].filter(
            a => a.id !== alertaId
          )
        }
        // Eliminar de items
        for (const item of state.items) {
          if (item.alertas) {
            item.alertas = item.alertas.filter(a => a.id !== alertaId)
          }
        }
      })
  }
})

export const { clearSelectedMaterial, clearError, setPage } = materialSlice.actions
export default materialSlice.reducer
